import { ChevronLeft, Star, X } from 'lucide-react'
import type { Invoice } from '@/types/invoice'
import {
  daysUntilDue, formatCurrency, isImportantEligible, isReminderDone, STATUS_CONFIG, TYPE_LABEL,
} from './invoiceUtils'

interface Props {
  invoice: Invoice
  /** Nummer des Belegs, zu dem der Zurück-Pfeil führt (null = oberste Ebene). */
  parentLabel: string | null
  openSum: number
  onBack: () => void
  onClose: () => void
  onToggleImportant: () => void
}

/**
 * Gepinnte Kopfleiste: zeigt jederzeit, in welchem Beleg man steckt.
 *
 * Die grosse Zahl ist der OFFENE Betrag, sobald eine Teilzahlung oder eine
 * verrechnete Gutschrift existiert — das ist der Wert, der beim Bezahlen zaehlt.
 */
export default function InvoiceHeaderBar({
  invoice, parentLabel, openSum, onBack, onClose, onToggleImportant,
}: Props) {
  const isReminder = invoice.type === 'reminder'
  const status = STATUS_CONFIG[invoice.status]
  const partial = Math.abs(openSum - (invoice.gross_amount || 0)) > 0.005 && invoice.status !== 'paid'

  const days = invoice.status === 'paid' ? null : daysUntilDue(invoice.due_date)
  const dueTag =
    days == null ? null
      : days < 0 ? { text: `${Math.abs(days)} Tage überfällig`, cls: 'bg-red-100 text-red-700' }
      : days <= 7 ? { text: days === 0 ? 'heute fällig' : `fällig in ${days} Tagen`, cls: 'bg-amber-100 text-amber-700' }
      : null

  const statusPill = isReminder
    ? {
        text: isReminderDone(invoice.status)
          ? (invoice.status === 'paid' ? 'Bezahlt' : 'Erledigt')
          : `Stufe ${invoice.reminder_level ?? '?'}`,
        cls: isReminderDone(invoice.status) ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700',
      }
    : { text: status?.label || invoice.status, cls: status?.color || 'bg-gray-100 text-gray-700' }

  return (
    <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-gray-200 bg-white px-4 py-2.5">
      {parentLabel && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold text-primary-600 transition-colors hover:bg-primary-50"
          title="Zurück"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="max-w-[160px] truncate">{parentLabel}</span>
          <span className="text-gray-300">›</span>
        </button>
      )}

      <span className="flex min-w-0 items-baseline gap-2">
        <span className={`flex-shrink-0 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
          isReminder ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-500'
        }`}>
          {isReminder ? `Mahnung · Stufe ${invoice.reminder_level ?? '?'}` : TYPE_LABEL[invoice.type] || invoice.type}
        </span>
        <span className="truncate text-base font-bold tracking-tight text-gray-900">
          {invoice.invoice_number || `#${invoice.id}`}
        </span>
      </span>

      <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
        {invoice.type === 'outgoing'
          ? (invoice.recipient_name || invoice.supplier_name)
          : invoice.supplier_name}
      </span>

      {dueTag && (
        <span className={`flex-shrink-0 whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold ${dueTag.cls}`}>
          {dueTag.text}
        </span>
      )}

      <span className={`flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${statusPill.cls}`}>
        {statusPill.text}
      </span>

      <span className="flex-shrink-0 text-right leading-tight">
        <b className="text-base font-bold tabular-nums text-gray-900">
          {formatCurrency(partial ? openSum : invoice.gross_amount)}
        </b>
        <span className="block text-[10px] text-gray-400">
          {partial ? `offen · von ${formatCurrency(invoice.gross_amount)}` : 'Bruttobetrag'}
        </span>
      </span>

      {isImportantEligible(invoice) && (
        <button
          type="button"
          onClick={onToggleImportant}
          aria-pressed={!!invoice.is_important}
          title={invoice.is_important ? 'Markierung entfernen' : 'Als wichtig markieren'}
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors hover:bg-gray-100 ${
            invoice.is_important ? 'text-accent-500' : 'text-gray-300'
          }`}
        >
          <Star className="h-4 w-4" fill={invoice.is_important ? 'currentColor' : 'none'} />
        </button>
      )}

      <button
        type="button"
        onClick={onClose}
        title="Schließen"
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
